// Generates dynamic standings columns based on schedule settings
import type { StatType } from '@sportninja/common/types'

interface TieBreakInfo {
  primaryId?: number
  tieBreakerIds?: number[]
}

interface StandingColumn {
  key: string
  title: string
  dataIndex: string
  sorter: (a: any, b: any) => number
  showSorterTooltip: {
    title: string
  }
  width?: number
  defaultSortOrder?: 'ascend' | 'descend'
}

/**
 * Helper function to calculate value difference respecting is_sort_desc flag
 * @param stat - StatType object containing is_sort_desc flag
 * @param a - First value object
 * @param b - Second value object
 * @returns Numeric difference adjusted for sort direction
 */
const valueDiff = (stat: StatType, a: any, b: any): number => {
  const aValue = Number(a[stat.abbreviation] ?? 0)
  const bValue = Number(b[stat.abbreviation] ?? 0)

  if (stat.is_sort_desc) {
    return aValue - bValue
  }

  if (!stat.is_sort_desc) {
    return aValue - bValue
  }

  return 0
}

const valueDiffTieBreaker = (stat: StatType, a: any, b: any): number => {
  const aValue = Number(a[stat.abbreviation] ?? 0)
  const bValue = Number(b[stat.abbreviation] ?? 0)

  if (stat.is_sort_desc) {
    return aValue - bValue
  }

  if (!stat.is_sort_desc) {
    return bValue - aValue
  }

  return 0
}

/**
 * Generates dynamic standings columns based on displayed stat IDs from settings
 * @param displayedIds - Array of stat IDs from settings.standings.displayed
 * @param statTypes - Array of stat type objects from the sport
 * @param tieBreakInfo - Optional tie-breaking configuration
 * @returns Array of Ant Design column configurations
 */
export const generateStandingColumns = (
  displayedIds: number[],
  statTypes: StatType[],
  tieBreakInfo?: TieBreakInfo
): StandingColumn[] => {
  if (!displayedIds?.length || !statTypes?.length) {
    return []
  }

  // Create a map for quick stat type lookup by ID
  const statTypeMap = new Map<number, StatType>()
  statTypes.forEach((stat) => {
    statTypeMap.set(stat.id, stat)
  })

  // Find primary stat for tie-breaking logic
  const primaryStat = tieBreakInfo?.primaryId
    ? statTypeMap.get(tieBreakInfo.primaryId)
    : null

  // Generate columns in the same order as displayedIds
  const columns: StandingColumn[] = displayedIds
    .map((id) => {
      const statType = statTypeMap.get(id)
      if (!statType) {
        console.warn(`Stat type with ID ${id} not found in statTypes`)
        return null
      }

      const { abbreviation, name, description } = statType
      const isPrimaryColumn =
        primaryStat && abbreviation === primaryStat.abbreviation

      // Special handling for IN-DIV column with custom sorter
      if (abbreviation === 'IN-DIV') {
        return {
          key: abbreviation,
          title: 'DIV',
          dataIndex: abbreviation,
          width: 150,
          showSorterTooltip: {
            title: 'In-Division Record (W-L-T-OTL-SOL)',
          },
          sorter: (a: any, b: any) => {
            const aValue = a[abbreviation]?.split('-')?.[0] || '0'
            const bValue = b[abbreviation]?.split('-')?.[0] || '0'
            return Number(aValue) - Number(bValue)
          },
        }
      }

      // Primary column (with or without tie-breaking logic)
      if (isPrimaryColumn) {
        const hasTieBreakers = (tieBreakInfo?.tieBreakerIds?.length ?? 0) > 0

        return {
          key: abbreviation,
          title: abbreviation,
          dataIndex: abbreviation,
          defaultSortOrder: statType.is_sort_desc ? 'descend' : 'ascend',
          sorter: (a: any, b: any) => {
            // Primary sort using valueDiff to respect is_sort_desc
            const primaryDiff = valueDiff(statType, a, b)
            if (primaryDiff !== 0 || !hasTieBreakers) {
              return primaryDiff
            }

            // Apply tie breakers in order (only if they exist)
            for (const tieBreakerStatId of tieBreakInfo?.tieBreakerIds ?? []) {
              const tieBreakerStat = statTypeMap.get(tieBreakerStatId)
              if (tieBreakerStat?.abbreviation) {
                const diff = valueDiffTieBreaker(tieBreakerStat, a, b)
                if (diff !== 0) {
                  return diff
                }
              }
            }
            return 0
          },
          showSorterTooltip: {
            title: description || name,
          },
        }
      }

      // Default column configuration (no tie-breaking)
      const column: StandingColumn = {
        key: abbreviation,
        title: abbreviation,
        dataIndex: abbreviation,
        sorter: (a: any, b: any) => valueDiff(statType, a, b),
        showSorterTooltip: {
          title: description || name,
        },
      }

      return column
    })
    .filter(Boolean) as StandingColumn[]

  // Calculate equal width for all dynamic columns (excluding IN-DIV which has fixed width)
  const regularColumns = columns.filter((col) => col.key !== 'IN-DIV')
  const inDivColumns = columns.filter((col) => col.key === 'IN-DIV')

  if (regularColumns.length > 0) {
    // Calculate available width for dynamic columns
    // Assuming total available width for dynamic columns is around 800px
    // Subtract fixed width columns (IN-DIV = 150px each)
    const totalAvailableWidth = 800
    const fixedWidthUsed = inDivColumns.length * 150
    const availableWidthForRegular = totalAvailableWidth - fixedWidthUsed
    const equalWidth = Math.max(
      60,
      Math.floor(availableWidthForRegular / regularColumns.length)
    )
    // Apply equal width to all regular columns
    regularColumns.forEach((column) => {
      column.width = equalWidth
    })
  }

  return columns
}

export default generateStandingColumns
